import AddCircleIcon from '@mui/icons-material/AddCircle';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import InputIcon from '@mui/icons-material/Input';
import React, { useContext } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { ApiContext } from '_lib/api/apiContext';
import { ActionBar } from '_lib/components/ActionBar/ActionBar';
import { Avatar } from '_lib/components/Avatar/Avatar';
import { Button } from '_lib/components/Button/Button';
import { Card } from '_lib/components/Card/Card';
import { CardContent } from '_lib/components/CardContent/CardContent';
import { Divider } from '_lib/components/Divider/Divider';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { IconButton } from '_lib/components/IconButton/IconButton';
import { LinkListItemButton } from '_lib/components/LinkListItemButton/LinkListItemButton';
import { List } from '_lib/components/List/List';
import { ListItem } from '_lib/components/ListItem/ListItem';
import { ListItemAvatar } from '_lib/components/ListItemAvatar/ListItemAvatar';
import { ListItemText } from '_lib/components/ListItemText/ListItemText';
import { Search } from '_lib/components/Search/Search';
import { Stack } from '_lib/components/Stack/Stack';
import { Tooltip } from '_lib/components/Tooltip/Tooltip';
import { Typography } from '_lib/components/Typography/Typography';
import { useQueryParams } from '_lib/hooks/useQueryParams';
import { formatAddressOneLine } from '_lib/utils/helpers';

import { SimpleAppBar } from '../../components/AppBar/SimpleAppBar';
import { AppDrawerState, AppRouteContext } from '../App/app.types';
import { AuthContext } from '../auth/authContext';
import { appRoutePaths, getFullAppRoutePath, getGoBackState } from '../Nav/nav.types';
import { RouteErrorBoundary } from '../Routing/RouteErrorBoundary';
import { ScrollableContentWrapper } from '../ScrollableContentWrapper/ScrollableContentWrapper';

interface CallCenterListProps {
  appDrawerState?: AppDrawerState;
}

const CallCenterList = (props: CallCenterListProps) => {
  const { appDrawerState } = props;

  const api = useContext(ApiContext);
  const { callCenterCacheResource } = useContext(AuthContext);

  const { params, setParam, searchParams } = useQueryParams({
    searchWords: { defaultValue: '' }
  });

  const callCenterQuery = api.callCenters.useUnpagedList({
    params
  });

  const navigate = useNavigate();

  if (callCenterQuery.error) {
    return <RouteErrorBoundary appBarTitle='Call Centers' errorTitle='Error Fetching Call Centers' />;
  }

  return (
    <FlexBox sx={{ flexDirection: 'column', height: '100%', width: '100%' }}>
      <SimpleAppBar title='Call Centers' onAppDrawerToggleClicked={appDrawerState?.openSlidingDrawer} />
      <ActionBar
        actions={
          <Button
            disableFocusRipple
            text='Add Call Center'
            variant='text'
            onClick={() => {
              navigate(`${getFullAppRoutePath(appRoutePaths.CallCenter)}/new`, {
                state: { goBack: getGoBackState(appRoutePaths.CallCenter, searchParams) }
              });
            }}
            startIcon={<AddCircleIcon />}
            sx={{ textTransform: 'none' }}
          />
        }
      />
      <Divider />
      <Stack direction='row' padding={2}>
        <Search initialValue={params.searchWords} onSearch={(searchWords) => setParam('searchWords', searchWords)} />
      </Stack>
      <Divider />
      <ScrollableContentWrapper isSavingOrLoading={callCenterQuery.isFetching}>
        {!callCenterQuery.isFetching && !callCenterQuery.data?.length && (
          <Card variant='outlined'>
            <CardContent>
              <Typography text='No Items were found.' />
            </CardContent>
          </Card>
        )}
        {!!callCenterQuery.data?.length && (
          <List sx={{ padding: 0 }}>
            {callCenterQuery.data.map((callCenter) => (
              <ListItem
                key={callCenter.id}
                divider
                disablePadding
                secondaryAction={
                  <Tooltip title='Switch to this Call Center' placement='left'>
                    <IconButton
                      onClick={async () => {
                        await callCenterCacheResource.set(callCenter.id).catch(() => {});
                        navigate(getFullAppRoutePath(appRoutePaths.Profile));
                      }}>
                      <InputIcon />
                    </IconButton>
                  </Tooltip>
                }>
                <LinkListItemButton
                  href={`${getFullAppRoutePath(appRoutePaths.CallCenter)}/${callCenter.id}`}
                  onClick={() => {
                    navigate(`${callCenter.id}`, {
                      state: { goBack: getGoBackState(appRoutePaths.CallCenter, searchParams) }
                    });
                  }}>
                  <ListItemAvatar>
                    <Avatar>
                      <HeadsetMicIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={callCenter.name}
                    secondary={formatAddressOneLine(callCenter, { includeCountry: true })}
                  />
                </LinkListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </ScrollableContentWrapper>
    </FlexBox>
  );
};

export const CallCenterListRoute = () => {
  const { appDrawerState } = useOutletContext<AppRouteContext>();
  return <CallCenterList appDrawerState={appDrawerState} />;
};
